import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Library, Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { designation: "", reference: "", marque: "", prix: 0 };

export default function Catalog() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const { data } = await api.get("/catalog");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => { setEditId(it.id); setForm({ designation: it.designation, reference: it.reference || "", marque: it.marque || "", prix: it.prix || 0 }); setOpen(true); };

  const save = async () => {
    const payload = { ...form, prix: Number(form.prix) || 0 };
    try {
      if (editId) await api.put(`/catalog/${editId}`, payload);
      else await api.post("/catalog", payload);
      toast.success(editId ? "Pièce mise à jour" : "Pièce ajoutée au catalogue");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette pièce du catalogue ?")) return;
    await api.delete(`/catalog/${id}`); toast.success("Pièce supprimée"); load();
  };

  const filtered = items.filter((it) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [it.designation, it.reference, it.marque].some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#F8FAFC] flex items-center gap-2">
            <Library className="w-7 h-7 text-cyan-400" /> Catalogue pièces
          </h1>
          <p className="text-[#94A3B8] text-sm">Référentiel des pièces à commander — réutilisé dans les fiches de réparation.</p>
        </div>
        <Button data-testid="add-catalog-button" onClick={openNew} className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0A0D14] font-semibold">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle pièce
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-[#64748B] absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid="catalog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (désignation, référence, marque)…" className="pl-9 bg-[#182232] border-[#2A364F]" />
      </div>

      <div className="bg-[#182232] border border-[#2A364F] rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="catalog-table">
          <thead>
            <tr className="text-[#64748B] text-xs uppercase tracking-wider border-b border-[#2A364F]">
              <th className="text-left px-4 py-3">Désignation</th>
              <th className="text-left px-4 py-3">Référence</th>
              <th className="text-left px-4 py-3">Marque</th>
              <th className="text-right px-4 py-3">Prix indicatif</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#2A364F]">
            {filtered.map((it) => (
              <tr key={it.id} data-testid={`catalog-row-${it.id}`} className="hover:bg-[#1E2A3E]">
                <td className="px-4 py-3 text-[#F8FAFC] font-medium">{it.designation}</td>
                <td className="px-4 py-3 font-mono text-xs text-cyan-400">{it.reference || "—"}</td>
                <td className="px-4 py-3 text-[#94A3B8]">{it.marque || "—"}</td>
                <td className="px-4 py-3 text-right font-mono text-[#F59E0B]">{it.prix ? `${Number(it.prix).toFixed(2)} €` : "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button data-testid={`edit-catalog-${it.id}`} onClick={() => openEdit(it)} className="w-8 h-8 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-[#3B82F6] flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`delete-catalog-${it.id}`} onClick={() => del(it.id)} className="w-8 h-8 rounded hover:bg-[#0A0D14] text-[#94A3B8] hover:text-[#EF4444] flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-[#64748B]">{items.length === 0 ? "Catalogue vide — ajoutez une pièce ou créez-en depuis une fiche." : "Aucun résultat."}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121824] border-[#2A364F] text-[#F8FAFC]">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Modifier la pièce" : "Nouvelle pièce du catalogue"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-[#94A3B8]">Désignation *</Label>
              <Input data-testid="catalog-designation" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" placeholder="Ex. Kit de distribution" />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Référence</Label>
              <Input data-testid="catalog-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F] font-mono" />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Marque</Label>
              <Input data-testid="catalog-marque" value={form.marque} onChange={(e) => setForm({ ...form, marque: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" />
            </div>
            <div>
              <Label className="text-[#94A3B8]">Prix indicatif (€)</Label>
              <Input data-testid="catalog-prix" type="number" step="0.01" value={form.prix} onChange={(e) => setForm({ ...form, prix: e.target.value })} className="mt-1 bg-[#182232] border-[#2A364F]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#2A364F] bg-transparent text-[#94A3B8]">Annuler</Button>
            <Button data-testid="save-catalog-button" onClick={save} disabled={!form.designation.trim()} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
